import { SageMakerLink } from 'src/components/SageMakerLink';
import { ARN, build } from '@aws-sdk/util-arn-parser';
import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from 'src/context/userContext';
import { fetchDeploymentJob } from 'src/components/fetchDeploymentJob';
import { Link } from 'src/components/Link';
import { OperatorInfo } from 'src/components/OperatorInfo';
import { StatusLabel } from 'src/components/StatusLabel';
import { OperatorModalContext } from 'src/context/operatorModalContext';
import {
  OperatorAction,
  OperatorActionMenu,
} from 'src/components/OperatorActionsMenu';
import {
  getRegionsFromArnList,
  getEndpointInfoFromArnList,
} from 'src/utils/operatorUtils';
import { useNavigate, useParams } from 'react-router-dom';

export interface DeploymentJobNodeProps {
  header: string;
  info: {
    id: string;
    operatorName: string;
    modelName?: string;
    modelArn?: ARN;
    operatorFlowId: string;
    region?: string;
  };
  status: OperatorJobStatus;
  arnList?: ARN[];
}

export const DeploymentJobNode = ({
  header,
  info,
  status,
  arnList,
}: DeploymentJobNodeProps) => {
  const { accountNo, applicationId, modelVersionId, modelArtifactId } =
    useParams<Record<string, string>>();

  const user = useContext(UserContext);
  const {
    setPromoteConfig,
    setRedeployConfig,
    setRetryConfig,
    setOverrideConfig,
  } = useContext(OperatorModalContext);

  const [deploySetting, setDeploySetting] = useState<
    'Deploy' | 'Redeploy' | null
  >(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const regions = getRegionsFromArnList(arnList);
  const endpointInfo = getEndpointInfoFromArnList(arnList);
  const navigate = useNavigate();

  const operatorActions: OperatorAction[] = [];

  if (status === 'FAILED') {
    operatorActions.push({
      name: 'Retry',
      description: 'retry operators',
      requiredPermission: 'READ_AND_LIMITED_ACTION',
      actionFn: () => {
        setRetryConfig({
          action: 'retry',
          id: info.operatorFlowId,
          operatorName: info.operatorName,
        });
      },
    });
  }

  if (status === 'FAILED' || status === 'PENDING') {
    operatorActions.push({
      name: 'Override',
      description: 'override operators',
      requiredPermission: 'ADMINISTER',
      actionFn: () => {
        setOverrideConfig({
          id: info.operatorFlowId,
          action: 'SKIPPEDOVERRIDE',
          operatorName: info.operatorName,
          operatorStatus: status,
        });
      },
    });
  }

  if (deploySetting === 'Deploy') {
    operatorActions.push({
      name: 'Deploy to Production',
      description: 'promote models',
      requiredPermission: 'ADMINISTER',
      actionFn: () => {
        setPromoteConfig({
          modelName: info.modelName!,
          arn: info.modelArn!,
        });
      },
    });
  }

  if (deploySetting === 'Redeploy') {
    operatorActions.push({
      name: 'Redeploy',
      description: 'redeploy models',
      requiredPermission: 'READ_AND_LIMITED_ACTION',
      actionFn: () => {
        if (endpointInfo?.arn) {
          navigate(
            `/accounts/${accountNo}/applications/${applicationId}/modelVersions/${modelVersionId}/modelArtifacts/${modelArtifactId}/endpoints/${encodeURIComponent(build(endpointInfo.arn))}?deploymentJobId=${info.id}`,
          );
        } else {
          setRedeployConfig({
            operatorName: info.operatorName,
            rollbackOperatorFlowId: info.operatorFlowId,
            regions,
          });
        }
      },
    });
  }

  useEffect(() => {
    if (status !== 'SUCCEEDED') return; // dont show any buttons if the job wasnt successful

    const checkDeploymentType = async () => {
      if (info.modelName?.startsWith('beta-')) {
        setLoading(true);
        await fetchDeploymentJob(info.id, user.token)
          .then((data) => {
            if (data?.stage === 'BETA') {
              setDeploySetting('Deploy');
            } else {
              setDeploySetting('Redeploy');
            }
          })
          .catch(setError)
          .finally(() => {
            setLoading(false);
          });

        return;
      }

      setDeploySetting('Redeploy'); // default to Redeploy
    };

    checkDeploymentType();
  }, [info.modelName]);

  return (
    <div className="job-visualization-node pointer-events-auto">
      <div className="job-visualization-node__header">
        <h3>{header}</h3>
        <div className="job-visualization-node__status-container">
          <StatusLabel loading={false} label={status} />
        </div>
      </div>
      <OperatorInfo
        operatorName={info.operatorName}
        operatorId={info.id}
        region={info.region}
      />
      <div className="job-visualization-node__links">
        {arnList?.map((arn) => <SageMakerLink key={build(arn)} arn={arn} />)}
        <Link to={`operatorHistory?operatorName=${info.operatorName}`}>
          View History
        </Link>
      </div>
      {error && <p className="error-text">{error.message}</p>}
      <div className="job-visualization-node__buttons">
        <div className="job-visualization-node__button-group">
          {status !== 'NOTSTARTED' && (
            <Link to={`../logs?operatorName=${info.operatorName}`} newTab>
              View Logs
            </Link>
          )}

          <OperatorActionMenu actions={operatorActions} loading={loading} />
        </div>
      </div>
    </div>
  );
};
